import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FounderNav } from '../components/FounderNav';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  fetchFounderDoc,
  fetchFounderDocs,
  type FounderDocFull,
  type FounderDocMeta,
} from '../lib/api';
import { resolveApiUrl } from '../lib/api-origin';
import { isNative, saveOrShareText } from '../lib/capacitor';

/**
 * Founder-only pilot-document binder. The docs/*.md pack is embedded in the
 * API build and served behind requireFounder (never bundled into the public
 * web app — these carry pricing / liability / legal drafts). Reads them in
 * app, and saves/shares them out:
 *   - web:    blob download
 *   - native: writes to cache + opens the OS share sheet (save to Files /
 *             Drive, email a school, etc.)
 *
 * Mobile uses a master-detail layout: the section list first, then the reader
 * full-width with a back affordance — the old two-column grid was unusable on
 * a phone.
 */
export function FounderDocs() {
  const [docs, setDocs] = useState<FounderDocMeta[] | null>(null);
  const [stale, setStale] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeSlug, setActiveSlug] = useState<string | null>(null);
  const [active, setActive] = useState<FounderDocFull | null>(null);
  const [loadingDoc, setLoadingDoc] = useState(false);
  // On a phone we show the list OR the reader, not both. Opening a doc flips
  // this true; the back button flips it false. Ignored on desktop (CSS).
  const [readerOpenMobile, setReaderOpenMobile] = useState(false);
  const [busyAll, setBusyAll] = useState(false);
  const [hint, setHint] = useState<string | null>(null);

  const native = isNative();

  useEffect(() => {
    void fetchFounderDocs().then((res) => {
      if (res.ok) {
        setDocs(res.data.docs);
        const first =
          res.data.docs.find((d) => d.slug === 'README')?.slug ?? res.data.docs[0]?.slug ?? null;
        setActiveSlug(first);
      } else if (res.stale) {
        setStale(true);
      } else {
        setError(res.error);
      }
    });
  }, []);

  useEffect(() => {
    if (!activeSlug) return;
    setLoadingDoc(true);
    setActive(null);
    void fetchFounderDoc(activeSlug).then((res) => {
      setLoadingDoc(false);
      if (res.ok) setActive(res.data);
      else if (res.stale) setStale(true);
      else setError(res.error);
    });
  }, [activeSlug]);

  const grouped = useMemo(() => {
    const out: { section: string; docs: FounderDocMeta[] }[] = [];
    for (const d of docs ?? []) {
      let g = out.find((x) => x.section === d.section);
      if (!g) {
        g = { section: d.section, docs: [] };
        out.push(g);
      }
      g.docs.push(d);
    }
    return out;
  }, [docs]);

  function openDoc(slug: string) {
    setActiveSlug(slug);
    setReaderOpenMobile(true);
    setHint(null);
  }

  async function saveActive() {
    if (!active) return;
    const result = await saveOrShareText(`${active.slug}.md`, active.markdown);
    flashHint(result);
  }

  async function saveAll() {
    setBusyAll(true);
    setHint(null);
    try {
      const res = await fetch(resolveApiUrl('/api/founder/docs-bundle'), {
        credentials: 'include',
      });
      if (!res.ok) {
        setHint('Could not load the binder. Try a fresh sign-in.');
        return;
      }
      const text = await res.text();
      const result = await saveOrShareText('lessalone-binder.md', text);
      flashHint(result);
    } catch {
      setHint('Could not prepare the download.');
    } finally {
      setBusyAll(false);
    }
  }

  function flashHint(result: 'shared' | 'downloaded' | 'failed') {
    if (result === 'failed') setHint('Could not save the file.');
    else if (result === 'shared') setHint('Opened the share sheet — save it to Files, Drive, or email.');
    else setHint('Downloaded.');
    if (result !== 'failed') window.setTimeout(() => setHint(null), 6000);
  }

  if (stale) {
    return (
      <main className="wide">
        <div className="card">
          <h1>Pilot binder</h1>
          <p>
            This needs a fresh sign-in. <Link to="/founder">Back to the founder dashboard</Link>,
            re-authenticate, then return here.
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="wide">
      <div className="card">
        <FounderNav />
        <div className="docs-head">
          <div>
            <h1>Pilot binder</h1>
            <p className="muted">
              The full doc pack — pitch, legal drafts, security answers, go-to-market. Founder-only;
              never shown to members.
            </p>
          </div>
          <button type="button" className="btn-sm" disabled={busyAll} onClick={() => void saveAll()}>
            {busyAll ? 'Preparing…' : native ? '↗ Share whole binder' : '↓ Download all (one file)'}
          </button>
        </div>
        {hint && <p className="docs-hint">{hint}</p>}
        {error && <p className="error">{error}</p>}
        {!docs && !error && <p className="muted">Loading…</p>}

        {docs && (
          <div className={`docs-layout${readerOpenMobile ? ' reader-open' : ''}`}>
            <nav className="docs-nav" aria-label="Documents">
              {grouped.map((group) => (
                <div key={group.section} className="docs-nav-group">
                  <span className="docs-nav-section">{group.section}</span>
                  {group.docs.map((d) => (
                    <button
                      key={d.slug}
                      type="button"
                      className={`docs-nav-item${d.slug === activeSlug ? ' on' : ''}`}
                      onClick={() => openDoc(d.slug)}
                    >
                      {d.title}
                    </button>
                  ))}
                </div>
              ))}
            </nav>

            <section className="docs-reader">
              <button
                type="button"
                className="docs-back btn-sm"
                onClick={() => setReaderOpenMobile(false)}
              >
                ← All documents
              </button>
              {loadingDoc && <p className="muted">Loading…</p>}
              {active && (
                <>
                  <div className="docs-reader-bar">
                    <span className="docs-reader-slug">{active.slug}.md</span>
                    <span className="docs-reader-actions">
                      <button type="button" className="btn-sm" onClick={() => void saveActive()}>
                        {native ? '↗ Save / share' : '↓ Download'}
                      </button>
                      <button type="button" className="btn-sm" onClick={() => window.print()}>
                        🖨 Print / PDF
                      </button>
                    </span>
                  </div>
                  <article className="docs-prose" id="docs-print-area">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{active.markdown}</ReactMarkdown>
                  </article>
                </>
              )}
            </section>
          </div>
        )}
      </div>
    </main>
  );
}
